/**
 * Tabs + Accordion Hybrid Component
 *
 * Behaviour:
 *   - Desktop (>= 768px / 48rem): ARIA tablist pattern, vertical layout.
 *     • Arrow keys (Up/Down) move focus between tabs.
 *     • Home / End jump to first / last tab.
 *     • Only one panel open at a time.
 *
 *   - Mobile / tablet (< 768px): ARIA accordion pattern.
 *     • Each trigger uses aria-expanded.
 *     • Panels open/close independently.
 *
 * The breakpoint matches $breakpoint-desktop-min in _variables.scss (48rem).
 *
 * HTML contract (output by tabs.php):
 *   [data-tabs]                   — component root
 *     .tabs__nav[aria-hidden]     — tablist wrapper (CSS: hidden on mobile)
 *       [role="tablist"]          — tablist
 *         [role="tab"]            — individual tab buttons
 *     .tabs__body                 — accordion items + shared panels
 *       [data-tabs-item]          — one item per panel
 *         .tabs__item-btn         — mobile accordion trigger
 *         [data-tabs-panel]       — shared panel (hidden by default)
 *
 * @since 2.0.0
 */

import { CONTENT_DESKTOP_MQ, REDUCED_MOTION_MQ } from './breakpoints.js'

/**
 * Initialise all tab blocks on the page.
 */
export function initTabs () {
  document.querySelectorAll('[data-tabs]').forEach(container => {
    initTabsInstance(container)
  })
}

/**
 * Initialise a single tabs container.
 *
 * @param {HTMLElement} container - The [data-tabs] root element.
 */
function initTabsInstance (container) {
  const tablistEl = container.querySelector('[role="tablist"]')
  const tabsNav = container.querySelector('.tabs__nav')
  const panelEls = Array.from(container.querySelectorAll('[data-tabs-panel]'))
  const itemBtns = Array.from(container.querySelectorAll('.tabs__item-btn'))

  if (!tablistEl || !panelEls.length) return

  const tabEls = Array.from(tablistEl.querySelectorAll('[role="tab"]'))

  // Keep track of the current mode so resize handler can avoid redundant setup.
  let currentMode = null // 'tabs' | 'accordion'

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  /**
   * Show a panel and hide all others.
   * In tab mode also updates aria-selected on tab buttons.
   *
   * @param {number} index
   * @param {boolean} isTabMode
   */
  function activatePanel (index, isTabMode) {
    panelEls.forEach((panel, i) => {
      const active = i === index

      if (active) {
        panel.removeAttribute('hidden')
        panel.removeAttribute('aria-hidden')
        panel.classList.add('is-open')
      } else {
        panel.setAttribute('hidden', '')
        panel.setAttribute('aria-hidden', 'true')
        panel.classList.remove('is-open')
      }
    })

    if (isTabMode) {
      tabEls.forEach((tab, i) => {
        const active = i === index
        tab.setAttribute('aria-selected', String(active))
        tab.setAttribute('tabindex', active ? '0' : '-1')
      })
    }
  }

  /**
   * Collapse all panels (default/reset state).
   */
  function collapseAll () {
    panelEls.forEach(panel => {
      panel.setAttribute('hidden', '')
      panel.classList.remove('is-open')
      // Clear any in-progress animation state.
      panel.style.height = ''
      panel.style.overflow = ''
      panel.style.transition = ''
    })
  }

  // -------------------------------------------------------------------------
  // Animation helper (accordion mode)
  // -------------------------------------------------------------------------

  /**
   * Animate a panel open or closed using the same scroll-height technique as
   * the main accordion component. Respects prefers-reduced-motion.
   *
   * @param {HTMLElement} panel        - The panel element.
   * @param {boolean}     shouldExpand - true = open, false = close.
   * @param {Function}    [callback]   - Called when animation finishes.
   */
  function animateTabPanel (panel, shouldExpand, callback) {
    const reducedMotion = REDUCED_MOTION_MQ.matches
    const duration = reducedMotion ? 0 : 400

    // Cancel any in-progress animation on this panel.
    if (panel._tabAnimTimer) {
      clearTimeout(panel._tabAnimTimer)
      panel._tabAnimTimer = null
    }
    if (panel._tabAnimHandler) {
      panel.removeEventListener('transitionend', panel._tabAnimHandler)
      panel._tabAnimHandler = null
    }

    let cleanupCalled = false
    const cleanup = () => {
      if (cleanupCalled) return
      cleanupCalled = true
      if (panel._tabAnimTimer) { clearTimeout(panel._tabAnimTimer); panel._tabAnimTimer = null }
      if (panel._tabAnimHandler) { panel.removeEventListener('transitionend', panel._tabAnimHandler); panel._tabAnimHandler = null }
      panel.style.height = ''
      panel.style.overflow = ''
      panel.style.transition = ''
      if (!shouldExpand) {
        panel.setAttribute('hidden', '')
        panel.classList.remove('is-open')
      }
      if (callback) callback()
    }

    if (duration === 0) {
      if (shouldExpand) {
        panel.removeAttribute('hidden')
        panel.classList.add('is-open')
      } else {
        panel.setAttribute('hidden', '')
        panel.classList.remove('is-open')
      }
      if (callback) callback()
      return
    }

    if (shouldExpand) {
      panel.removeAttribute('hidden')
      panel.classList.add('is-open')
      panel.style.overflow = 'hidden'
      panel.style.height = '0px'

      requestAnimationFrame(() => {
        const target = panel.scrollHeight
        requestAnimationFrame(() => {
          panel.style.transition = `height ${duration}ms ease-out`
          panel.style.height = target + 'px'
        })
      })
    } else {
      panel.style.overflow = 'hidden'
      panel.style.height = panel.scrollHeight + 'px'

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          panel.style.transition = `height ${duration}ms ease-out`
          panel.style.height = '0px'
        })
      })
    }

    panel._tabAnimHandler = cleanup
    panel.addEventListener('transitionend', panel._tabAnimHandler, { once: true })
    panel._tabAnimTimer = setTimeout(cleanup, duration + 100)
  }

  // -------------------------------------------------------------------------
  // Tab mode (desktop)
  // -------------------------------------------------------------------------

  function setupTabMode () {
    if (currentMode === 'tabs') return
    currentMode = 'tabs'

    // Expose the tablist to assistive technology.
    tabsNav.removeAttribute('aria-hidden')
    tablistEl.removeAttribute('aria-hidden')

    // Reset tab button ARIA attributes.
    tabEls.forEach((tab, i) => {
      tab.setAttribute('role', 'tab')
      tab.setAttribute('aria-selected', 'false')
      tab.setAttribute('tabindex', '-1')
    })

    // Reset accordion trigger ARIA attributes (keep them hidden from AT).
    itemBtns.forEach(btn => {
      btn.setAttribute('aria-expanded', 'false')
      btn.removeAttribute('tabindex')
    })

    // Reset panels: remove tabpanel labelledby associations set in accordion mode.
    panelEls.forEach(panel => {
      panel.setAttribute('role', 'tabpanel')
    })

    // Collapse all panels then activate the first tab by default.
    collapseAll()
    activatePanel(0, true)

    // Attach tab click handler.
    tabEls.forEach((tab, index) => {
      tab.onclick = () => {
        activatePanel(index, true)
        tab.focus()
      }
    })

    // Keyboard navigation: Up/Down arrows, Home, End.
    tablistEl.onkeydown = (e) => {
      const active = tabEls.findIndex(t => t.getAttribute('aria-selected') === 'true')
      let next = active

      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault()
        next = (active + 1) % tabEls.length
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault()
        next = (active - 1 + tabEls.length) % tabEls.length
      } else if (e.key === 'Home') {
        e.preventDefault()
        next = 0
      } else if (e.key === 'End') {
        e.preventDefault()
        next = tabEls.length - 1
      } else {
        return
      }

      activatePanel(next, true)
      tabEls[next].focus()
    }
  }

  // -------------------------------------------------------------------------
  // Accordion mode (mobile / tablet)
  // -------------------------------------------------------------------------

  function setupAccordionMode () {
    if (currentMode === 'accordion') return
    currentMode = 'accordion'

    // Hide the tablist from assistive technology.
    tabsNav.setAttribute('aria-hidden', 'true')
    tablistEl.setAttribute('aria-hidden', 'true')

    // Detach tab keyboard handler.
    tablistEl.onkeydown = null

    // Clear tab selection.
    tabEls.forEach(tab => {
      tab.setAttribute('aria-selected', 'false')
      tab.setAttribute('tabindex', '-1')
      tab.onclick = null
    })

    // Update panel roles: tabpanel is only valid when associated with a visible tab.
    // In accordion mode remove the role so panels are plain regions.
    panelEls.forEach(panel => {
      panel.removeAttribute('role')
    })

    // Collapse all panels.
    collapseAll()

    // Open the first panel by default.
    if (panelEls[0]) {
      panelEls[0].removeAttribute('hidden')
      panelEls[0].classList.add('is-open')
      if (itemBtns[0]) {
        itemBtns[0].setAttribute('aria-expanded', 'true')
      }
    }

    // Attach accordion toggle handlers.
    itemBtns.forEach((btn, index) => {
      btn.onclick = () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true'
        const panel = panelEls[index]

        if (!panel) return

        if (expanded) {
          // Collapse this panel with animation.
          btn.setAttribute('aria-expanded', 'false')
          animateTabPanel(panel, false)
        } else {
          // Close all other open panels first (single-open behaviour).
          itemBtns.forEach((otherBtn, otherIndex) => {
            if (otherIndex !== index && otherBtn.getAttribute('aria-expanded') === 'true') {
              otherBtn.setAttribute('aria-expanded', 'false')
              if (panelEls[otherIndex]) {
                animateTabPanel(panelEls[otherIndex], false)
              }
            }
          })

          // Open this panel with animation.
          btn.setAttribute('aria-expanded', 'true')
          animateTabPanel(panel, true)
        }

        // Blur the button so it doesn't retain focus after the click.
        // Without this, moving the mouse back over the button triggers :hover
        // on a focused element, producing a visible style artifact on touch devices.
        btn.blur()
      }
    })
  }

  // -------------------------------------------------------------------------
  // Initial setup
  // -------------------------------------------------------------------------

  if (CONTENT_DESKTOP_MQ.matches) {
    setupTabMode()
  } else {
    setupAccordionMode()
  }

  // -------------------------------------------------------------------------
  // Responsive: switch mode on breakpoint crossing
  // -------------------------------------------------------------------------

  CONTENT_DESKTOP_MQ.addEventListener('change', (e) => {
    if (e.matches) {
      setupTabMode()
    } else {
      setupAccordionMode()
    }
  })
}
